import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["children", "onDismiss", "className", "onBack"];
import React, { useEffect, useState } from 'react';
import { Transition } from 'react-transition-group';
import { Button } from '../Button';
import { useNavigableDialog } from '../Dialog/useNavigableDialog';
import { withFlowWebContext } from '../FlowWebProvider/withFlowWebContext';
import { CloseIcon, MobileBackIcon } from '../icons';
import { Overlay } from '../Overlay';
import { useTranslate } from '../util';
import { forwardDataAttributes, setDataAttributes } from '../util/getDataAttributes';
import { StyledBackDiv, StyledCloseDiv, StyledDialog, StyledDialogFooter, StyledDialogHeader, StyledDialogTitle, StyledMainRegion } from './Dialog.styled';
export function _Dialog(props) {
  var onBack = props.onBack,
    onDismiss = props.onDismiss,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    _props$position = props.position,
    position = _props$position === void 0 ? 'centered' : _props$position,
    _props$closeOnEscape = props.closeOnEscape,
    closeOnEscape = _props$closeOnEscape === void 0 ? true : _props$closeOnEscape,
    _props$isRouted = props.isRouted,
    isRouted = _props$isRouted === void 0 ? false : _props$isRouted,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? true : _props$autoFocus,
    history = props.flowWebConfig.history;
  var dialogRef = /*#__PURE__*/React.createRef();
  var focusRef = /*#__PURE__*/React.createRef();
  var elementFocusedOnOpen = document.activeElement;
  var removeHistoryListener = history ? history.listen(close) : undefined;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isClosing = _useState2[0],
    setIsClosing = _useState2[1];
  useEffect(function () {
    if (autoFocus) {
      var _focusRef$current;
      (_focusRef$current = focusRef.current) === null || _focusRef$current === void 0 ? void 0 : _focusRef$current.focus();
    }
    return function () {
      var _elementFocusedOnOpen;
      elementFocusedOnOpen === null || elementFocusedOnOpen === void 0 ? void 0 : (_elementFocusedOnOpen = elementFocusedOnOpen.focus) === null || _elementFocusedOnOpen === void 0 ? void 0 : _elementFocusedOnOpen.call(elementFocusedOnOpen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);
  useEffect(function () {
    function handleKeyDown(e) {
      if (closeOnEscape && e.code === 'Escape') {
        close();
      }
    }
    window.addEventListener('keydown', handleKeyDown, true);
    function handlePopState() {
      onBack ? onBack() : close();
    }
    if (!isRouted) {
      window.history.pushState({}, 'emptyState');
      window.addEventListener('popstate', handlePopState);
    }
    return function () {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (!isRouted) {
        window.removeEventListener('popstate', handlePopState);
        removeHistoryListener === null || removeHistoryListener === void 0 ? void 0 : removeHistoryListener();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBack, closeOnEscape, isRouted, isClosing]);
  return /*#__PURE__*/React.createElement(Transition, {
    appear: true,
    "in": props.isOpen,
    timeout: 0
  }, function (animationState) {
    return /*#__PURE__*/React.createElement(Overlay, {
      position: props.position,
      isOpen: props.isOpen || false,
      onDismiss: close,
      overlayClassName: props.overlayClassName,
      closeOnOverlayClick: false
    }, /*#__PURE__*/React.createElement("div", {
      tabIndex: 0,
      onFocus: focusLast
    }), /*#__PURE__*/React.createElement(StyledDialog, _extends({
      animationState: animationState,
      position: position,
      className: className,
      ref: dialogRef,
      onChange: function onChange(e) {
        return stopIfNotBubbled('onChange', e);
      },
      onClick: function onClick(e) {
        return stopIfNotBubbled('onClick', e);
      },
      onContextMenu: function onContextMenu(e) {
        return stopIfNotBubbled('onContextMenu', e);
      },
      onDoubleClick: function onDoubleClick(e) {
        return stopIfNotBubbled('onDoubleClick', e);
      },
      onDrag: function onDrag(e) {
        return stopIfNotBubbled('onDrag', e);
      },
      onDragEnd: function onDragEnd(e) {
        return stopIfNotBubbled('onDragEnd', e);
      },
      onDragEnter: function onDragEnter(e) {
        return stopIfNotBubbled('onDragEnter', e);
      },
      onDragExit: function onDragExit(e) {
        return stopIfNotBubbled('onDragExit', e);
      },
      onDragLeave: function onDragLeave(e) {
        return stopIfNotBubbled('onDragLeave', e);
      },
      onDragOver: function onDragOver(e) {
        return stopIfNotBubbled('onDragOver', e);
      },
      onDragStart: function onDragStart(e) {
        return stopIfNotBubbled('onDragStart', e);
      },
      onDrop: function onDrop(e) {
        return stopIfNotBubbled('onDrop', e);
      },
      onInput: function onInput(e) {
        return stopIfNotBubbled('onInput', e);
      },
      onInvalid: function onInvalid(e) {
        return stopIfNotBubbled('onInvalid', e);
      },
      onKeyDown: function onKeyDown(e) {
        return stopIfNotBubbled('onKeyDown', e);
      },
      onKeyPress: function onKeyPress(e) {
        return stopIfNotBubbled('onKeyPress', e);
      },
      onKeyUp: function onKeyUp(e) {
        return stopIfNotBubbled('onKeyUp', e);
      },
      onMouseDown: function onMouseDown(e) {
        return stopIfNotBubbled('onMouseDown', e);
      },
      onMouseEnter: function onMouseEnter(e) {
        return stopIfNotBubbled('onMouseEnter', e);
      },
      onMouseLeave: function onMouseLeave(e) {
        return stopIfNotBubbled('onMouseLeave', e);
      },
      onMouseMove: function onMouseMove(e) {
        return stopIfNotBubbled('onMouseMove', e);
      },
      onMouseOut: function onMouseOut(e) {
        return stopIfNotBubbled('onMouseOut', e);
      },
      onMouseOver: function onMouseOver(e) {
        return stopIfNotBubbled('onMouseOver', e);
      },
      onMouseUp: function onMouseUp(e) {
        return stopIfNotBubbled('onMouseUp', e);
      }
    }, setDataAttributes(props, 'dialog')), /*#__PURE__*/React.createElement("div", {
      tabIndex: -1,
      ref: focusRef
    }), props.isFullScreen ? props.children : /*#__PURE__*/React.createElement(React.Fragment, null, props.title && /*#__PURE__*/React.createElement(DialogHeader, _extends({
      onBack: props.onBack,
      onDismiss: close
    }, forwardDataAttributes(props)), /*#__PURE__*/React.createElement(DialogTitle, {
      showBack: !!props.onBack
    }, props.title)), props.children)), /*#__PURE__*/React.createElement("div", {
      tabIndex: 0,
      onFocus: focusFirst
    }));
  });
  function close() {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    tryDismiss();
  }
  function tryDismiss() {
    if (!onDismiss) {
      throw new Error('[flow-web] the Dialog component expects the onDismiss prop to be supplied from the component passed to showDialog. ' + 'Did you forget to spread props into Dialog?');
    }
    onDismiss();
  }
  function stopIfNotBubbled(eventType, e) {
    var _props$bubbleEvents;
    if ((_props$bubbleEvents = props.bubbleEvents) !== null && _props$bubbleEvents !== void 0 && _props$bubbleEvents.includes(eventType)) {
      return;
    }
    e.stopPropagation();
  }
  function focusFirst() {
    var elements = getFocusableElements();
    if (elements) {
      var firstElement = elements[0];
      firstElement.focus();
    }
  }
  function focusLast() {
    var elements = getFocusableElements();
    if (elements) {
      var lastElement = elements[elements.length - 1];
      lastElement.focus();
    }
  }
  function getFocusableElements() {
    var _dialogRef$current;
    return (_dialogRef$current = dialogRef.current) === null || _dialogRef$current === void 0 ? void 0 : _dialogRef$current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  }
}

/**
 * UNSTABLE
 *
 * This API is likely to change. This component is not recommended for general use.
 * In most cases you should use Modal instead.
 */
export var Dialog = withFlowWebContext(_Dialog);
/**
 * UNSTABLE
 *
 * This API is likely to change. This component is not recommended for general use.
 * In most cases you should use Modal instead.
 */
export function NavigableDialog(props) {
  var content = useNavigableDialog(props.initContent, props);
  return /*#__PURE__*/React.createElement(Dialog, _extends({}, content.dialogProps, {
    isOpen: props.isOpen
  }), /*#__PURE__*/React.createElement(content.Component, _extends({}, content.dialogProps, {
    replaceContent: content.replaceContent
  })));
}
export function DialogHeader(_ref) {
  var children = _ref.children,
    onDismiss = _ref.onDismiss,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    onBack = _ref.onBack,
    props = _objectWithoutProperties(_ref, _excluded);
  var t = useTranslate();
  return /*#__PURE__*/React.createElement(StyledDialogHeader, {
    className: className
  }, onBack && /*#__PURE__*/React.createElement(StyledBackDiv, null, /*#__PURE__*/React.createElement(Button.IconOnly, _extends({
    type: "button",
    title: t('citrix-ui:back'),
    onClick: onBack,
    icon: MobileBackIcon,
    size: 22
  }, setDataAttributes(props, 'dialog-back')))), onDismiss && /*#__PURE__*/React.createElement(StyledCloseDiv, null, /*#__PURE__*/React.createElement(Button.IconOnly, _extends({
    type: "button",
    title: t('citrix-ui:close'),
    onClick: onDismiss,
    icon: CloseIcon,
    size: 22
  }, setDataAttributes(props, 'dialog-close')))), children);
}
export function DialogBody(_ref2) {
  var children = _ref2.children,
    _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? '' : _ref2$className;
  return /*#__PURE__*/React.createElement(StyledMainRegion, {
    className: className
  }, children);
}
export function DialogFooter(_ref3) {
  var children = _ref3.children,
    _ref3$className = _ref3.className,
    className = _ref3$className === void 0 ? '' : _ref3$className;
  return /*#__PURE__*/React.createElement(StyledDialogFooter, {
    className: className
  }, children);
}
export function DialogTitle(_ref4) {
  var children = _ref4.children,
    _ref4$className = _ref4.className,
    className = _ref4$className === void 0 ? '' : _ref4$className,
    showBack = _ref4.showBack;
  return /*#__PURE__*/React.createElement(StyledDialogTitle, {
    showBack: showBack,
    className: className
  }, children);
}